import * as Styled from "../../CreateAuthServiceModal.styled";
import { Modal } from "react-bootstrap";

import { Row, Col } from "react-bootstrap";

import FormControl from "@mui/material/FormControl";
import { Controller, useFormContext } from "react-hook-form";
import {
    Box,
    Divider,
    MenuItem,
    Select,
    Stack,
    styled,
    TextareaAutosize,
    TextField,
} from "@mui/material";
import FormAccordion from "components/molecules/FormAccordion";
import AddNewFields from "components/Leads/AddNewFields";
import { useCallback, useEffect, useRef } from "react";
import { blue, grey } from "@mui/material/colors";
import {
    renderBearerInputs,
    renderNoAuthInputs,
    renderBasicInputs,
    renderApiKeyInputs,
} from "./Content.helpers";

const methodOptions = ["GET", "PATCH", "POST", "PUT"];
const authHeaderTypes = ["BEARER", "NOAUTH", "BASIC", "APIKEY"];

const BodyDataCount = {
    BEARER: 1,
    NOAUTH: 0,
    BASIC: 2,
    APIKEY: 1,
};

const Textarea = styled(TextareaAutosize)(
    ({ theme }) => `
  box-sizing: border-box;
  width: 100%;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  padding: 8px 12px;
  border-radius: 8px;
  color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
  background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
  border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
  box-shadow: 0px 2px 2px ${
      theme.palette.mode === "dark" ? grey[900] : grey[50]
  };

  &:hover {
    border-color: ${blue[400]};
  }

  &:focus {
    border-color: ${blue[400]};
    box-shadow: 0 0 0 3px ${
        theme.palette.mode === "dark" ? blue[600] : blue[200]
    };
  }

  // firefox
  &:focus-visible {
    outline: 0;
  }
`,
);

const Content = ({
    testResult,
    isAuthHeaderChecked,
    setIsBodyChecked,
    setIsAuthHeaderChecked,
    isBodyChecked,
    originalAuthValues = [],
    onAuthValueChange,
    onResetTouchedIndices,
    isEditMode = false,
}) => {
    const {
        register,
        control,
        watch,
        setValue,
        clearErrors,
        formState: { errors },
    } = useFormContext();

    const authHeaderType = watch("auth_header.type");
    const authHeaderValues = watch("auth_header.values");

    const handleFieldFocus = useCallback(
        (index, originalValue) => {
            if (
                isEditMode &&
                originalValue &&
                !clearedIndices.current.has(index)
            ) {
                // Clear all auth header values when any input is focused
                const requiredCount = BodyDataCount[authHeaderType] || 0;
                const newValues = Array(requiredCount)
                    .fill(null)
                    .map(() => ({ field: "" }));

                setValue("auth_header.values", newValues);

                // Mark all indices as cleared
                for (let i = 0; i < requiredCount; i++) {
                    clearedIndices.current.add(i);
                }

                return true;
            }
            return false;
        },
        [isEditMode, authHeaderType, setValue],
    );
    const tokenExpiryRule = watch("expected_response.token_expiry_rule");

    const bodyType = watch("body.type");
    const bodyData = watch("body.data");

    const previousAuthHeaderType = useRef(authHeaderType);
    const previousBodyType = useRef(bodyType);
    const isInitialMount = useRef(true);
    const clearedIndices = useRef(new Set());

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            previousAuthHeaderType.current = authHeaderType;
            previousBodyType.current = bodyType;
            return;
        }

        if (
            authHeaderType &&
            authHeaderType !== previousAuthHeaderType.current
        ) {
            const requiredCount = BodyDataCount[authHeaderType];

            // Clear all values when type changes
            const newValues = Array(requiredCount)
                .fill(null)
                .map(() => ({ field: "" }));

            setValue("auth_header.values", newValues);
            previousAuthHeaderType.current = authHeaderType;

            clearErrors("auth_header.values");

            clearedIndices.current = new Set();

            if (isEditMode && onResetTouchedIndices) {
                onResetTouchedIndices();
            }
        }
    }, [
        authHeaderType,
        authHeaderValues,
        bodyType,
        setValue,
        clearErrors,
        isEditMode,
        onResetTouchedIndices,
    ]);

    useEffect(() => {
        if (previousBodyType.current === bodyType) {
            return;
        }

        if (bodyType) {
            const isCurrentlyJSON = bodyType === "JSON";
            const wasJSON = previousBodyType.current === "JSON";

            if (isCurrentlyJSON !== wasJSON) {
                setValue(
                    "body.data",
                    isCurrentlyJSON
                        ? ""
                        : [
                              {
                                  field: "",
                                  value: "",
                              },
                          ],
                );
            }
            previousBodyType.current = bodyType;
        }
    }, [bodyType, setValue]);

    return (
        <Modal.Body>
            <Styled.ModalBodyInnerContainer>
                <Row className="p-0">
                    <Col xs={12}>
                        <FormControl fullWidth className="mb-3">
                            <TextField
                                label="Auth Service Name"
                                {...register("name", {
                                    required: "Auth service name is required",
                                })}
                                error={!!errors.name}
                                helperText={errors.name?.message as string}
                                style={{
                                    width: "100%",
                                }}
                                className="text-field-ui-workaround"
                                required
                            />
                        </FormControl>
                    </Col>
                    <Col xs={6}>
                        <FormControl fullWidth className="mb-3">
                            <TextField
                                label="Service URL"
                                {...register("url", {
                                    required: "Service URL is required",
                                    pattern: {
                                        value: /^https?:\/\/.+/i,
                                        message:
                                            "Please enter a valid URL (http:// or https://)",
                                    },
                                })}
                                error={!!errors.url}
                                helperText={errors.url?.message as string}
                                style={{
                                    width: "100%",
                                }}
                                className="text-field-ui-workaround"
                                required
                            />
                        </FormControl>
                    </Col>
                    <Col xs={6}>
                        <FormControl
                            fullWidth
                            className="mb-3"
                            error={!!errors.method}
                        >
                            <Controller
                                control={control}
                                name="method"
                                rules={{
                                    required: "Method is required",
                                }}
                                render={({
                                    field: { onChange, name, value },
                                    fieldState: { error },
                                }) => (
                                    <>
                                        <Select
                                            label="Method"
                                            name={name}
                                            value={value || ""}
                                            onChange={onChange}
                                            error={!!error}
                                            required
                                        >
                                            {methodOptions?.map((value) => (
                                                <MenuItem
                                                    value={value}
                                                    key={value}
                                                >
                                                    {value}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {error && (
                                            <Box
                                                color="error.main"
                                                fontSize="0.75rem"
                                                mt={0.5}
                                                ml={1.5}
                                            >
                                                {error.message}
                                            </Box>
                                        )}
                                    </>
                                )}
                            />
                        </FormControl>
                    </Col>
                    <Col xs={12} className="mb-3">
                        <FormAccordion
                            isChecked={isAuthHeaderChecked}
                            onChange={setIsAuthHeaderChecked}
                            title={
                                <Stack direction="row">
                                    Auth Header{" "}
                                    <Box className="ml-1" color={"#ccc"}>
                                        (optional)
                                    </Box>
                                </Stack>
                            }
                        >
                            <Row className="p-0">
                                <Col xs={6}>
                                    <FormControl
                                        fullWidth
                                        className="mb-3 form-control-bg-white"
                                    >
                                        <TextField
                                            label="Name"
                                            {...register("auth_header.key", {
                                                validate: (value) => {
                                                    if (
                                                        isAuthHeaderChecked &&
                                                        !value
                                                    ) {
                                                        return "Auth header name is required when auth header is enabled";
                                                    }
                                                    return true;
                                                },
                                            })}
                                            error={
                                                !!(errors.auth_header as any)
                                                    ?.key
                                            }
                                            helperText={
                                                (errors.auth_header as any)?.key
                                                    ?.message as string
                                            }
                                            style={{
                                                width: "100%",
                                            }}
                                            className="text-field-ui-workaround"
                                        />
                                    </FormControl>
                                </Col>
                                <Col xs={6}>
                                    <FormControl
                                        fullWidth
                                        className="mb-3 form-control-bg-white"
                                        error={
                                            !!(errors.auth_header as any)?.type
                                        }
                                    >
                                        <Controller
                                            control={control}
                                            name="auth_header.type"
                                            rules={{
                                                validate: (value) => {
                                                    if (
                                                        isAuthHeaderChecked &&
                                                        !value
                                                    ) {
                                                        return "Auth header type is required when auth header is enabled";
                                                    }
                                                    return true;
                                                },
                                            }}
                                            render={({
                                                field: {
                                                    onChange,
                                                    name,
                                                    value,
                                                },
                                                fieldState: { error },
                                            }) => (
                                                <>
                                                    <Select
                                                        label="Type"
                                                        name={name}
                                                        value={value || ""}
                                                        onChange={onChange}
                                                        error={!!error}
                                                        fullWidth
                                                    >
                                                        {authHeaderTypes?.map(
                                                            (value) => (
                                                                <MenuItem
                                                                    value={
                                                                        value
                                                                    }
                                                                    key={value}
                                                                >
                                                                    {value}
                                                                </MenuItem>
                                                            ),
                                                        )}
                                                    </Select>
                                                    {error && (
                                                        <Box
                                                            color="error.main"
                                                            fontSize="0.75rem"
                                                            mt={0.5}
                                                            ml={1.5}
                                                        >
                                                            {error.message}
                                                        </Box>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </FormControl>
                                </Col>

                                {authHeaderType && (
                                    <Col xs={12}>
                                        {authHeaderType === "BEARER" &&
                                            renderBearerInputs({
                                                register,
                                                originalValues:
                                                    originalAuthValues,
                                                onFieldChange:
                                                    onAuthValueChange,
                                                onFieldFocus: handleFieldFocus,
                                                isEditMode,
                                                errors,
                                            })}
                                        {authHeaderType === "BASIC" &&
                                            renderBasicInputs({
                                                register,
                                                originalValues:
                                                    originalAuthValues,
                                                onFieldChange:
                                                    onAuthValueChange,
                                                onFieldFocus: handleFieldFocus,
                                                isEditMode,
                                                errors,
                                            })}
                                        {authHeaderType === "APIKEY" &&
                                            renderApiKeyInputs({
                                                register,
                                                originalValues:
                                                    originalAuthValues,
                                                onFieldChange:
                                                    onAuthValueChange,
                                                onFieldFocus: handleFieldFocus,
                                                isEditMode,
                                                errors,
                                            })}
                                        {authHeaderType === "NOAUTH" &&
                                            renderNoAuthInputs()}
                                    </Col>
                                )}
                            </Row>
                        </FormAccordion>
                    </Col>
                    <Col xs={12}>
                        <FormAccordion
                            isChecked={isBodyChecked}
                            onChange={setIsBodyChecked}
                            initalOpen={false}
                            title={
                                <Stack direction="row">
                                    Body{" "}
                                    <Box className="ml-1" color={"#ccc"}>
                                        (optional)
                                    </Box>
                                </Stack>
                            }
                        >
                            <Row className="p-0">
                                <Col xs={6}>
                                    <FormControl
                                        fullWidth
                                        className="mb-3 form-control-bg-white"
                                        error={!!(errors.body as any)?.type}
                                    >
                                        <Controller
                                            control={control}
                                            name="body.type"
                                            rules={{
                                                validate: (value) => {
                                                    if (
                                                        isBodyChecked &&
                                                        !value
                                                    ) {
                                                        return "Body type is required when body is enabled";
                                                    }
                                                    return true;
                                                },
                                            }}
                                            render={({
                                                field: {
                                                    onChange,
                                                    name,
                                                    value,
                                                },
                                                fieldState: { error },
                                            }) => (
                                                <>
                                                    <Select
                                                        label="Type"
                                                        name={name}
                                                        value={value || ""}
                                                        onChange={onChange}
                                                        error={!!error}
                                                        fullWidth
                                                    >
                                                        {[
                                                            "JSON",
                                                            "FORM_PARAMS",
                                                        ]?.map((value) => (
                                                            <MenuItem
                                                                value={value}
                                                                key={value}
                                                            >
                                                                {value}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {error && (
                                                        <Box
                                                            color="error.main"
                                                            fontSize="0.75rem"
                                                            mt={0.5}
                                                            ml={1.5}
                                                        >
                                                            {error.message}
                                                        </Box>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </FormControl>
                                </Col>
                                {bodyType === "JSON" && (
                                    <Col xs={12}>
                                        <Textarea
                                            minRows={5}
                                            placeholder={"Enter valid JSON"}
                                            {...register("body.data", {
                                                validate: (value) => {
                                                    if (
                                                        isBodyChecked &&
                                                        bodyType === "JSON"
                                                    ) {
                                                        if (
                                                            !value ||
                                                            value.trim() === ""
                                                        ) {
                                                            return "JSON body data is required when body type is JSON";
                                                        }
                                                        try {
                                                            JSON.parse(value);
                                                            return true;
                                                        } catch (e) {
                                                            return "Please enter valid JSON";
                                                        }
                                                    }
                                                    return true;
                                                },
                                            })}
                                        />
                                        {(errors.body as any)?.data && (
                                            <Box
                                                color="error.main"
                                                fontSize="0.75rem"
                                                mt={0.5}
                                                ml={1.5}
                                            >
                                                {
                                                    (errors.body as any)?.data
                                                        ?.message
                                                }
                                            </Box>
                                        )}
                                    </Col>
                                )}
                                {bodyType === "FORM_PARAMS" &&
                                    Array.isArray(bodyData) && (
                                        <Col xs={12}>
                                            <AddNewFields
                                                error={{}}
                                                fieldList={watch("body.data")}
                                                setFieldList={(data) =>
                                                    setValue("body.data", data)
                                                }
                                                renderRequired={false}
                                                title="Data"
                                                keyLabel="Key"
                                                valueLabel="Value"
                                            />
                                        </Col>
                                    )}
                            </Row>
                        </FormAccordion>
                    </Col>
                    <Col xs={12} className="mt-3">
                        <FormAccordion
                            onChange={setIsBodyChecked}
                            initalOpen={false}
                            title={
                                <Stack direction="row">Expected Response</Stack>
                            }
                            withCheckBox={false}
                        >
                            <Row className="p-0">
                                <Col xs={6}>
                                    <FormControl
                                        fullWidth
                                        className="mb-3 form-control-bg-white"
                                        error={
                                            !!(errors.expected_response as any)
                                                ?.token_expiry_rule
                                        }
                                    >
                                        <Controller
                                            control={control}
                                            name="expected_response.token_expiry_rule"
                                            rules={{
                                                required:
                                                    "Token expiry rule is required",
                                            }}
                                            render={({
                                                field: {
                                                    onChange,
                                                    name,
                                                    value,
                                                },
                                                fieldState: { error },
                                            }) => (
                                                <>
                                                    <Select
                                                        label="Token Expiry Rule"
                                                        name={name}
                                                        value={value || ""}
                                                        onChange={onChange}
                                                        error={!!error}
                                                        fullWidth
                                                        required
                                                    >
                                                        {[
                                                            {
                                                                label: "None",
                                                                value: "none",
                                                            },
                                                            {
                                                                label: "Static",
                                                                value: "static",
                                                            },
                                                            {
                                                                label: "Dynamic Expires In",
                                                                value: "dynamic_expires_in",
                                                            },
                                                            {
                                                                label: "Dynamic Expires At",
                                                                value: "dynamic_expires_at",
                                                            },
                                                        ]?.map(
                                                            ({
                                                                label,
                                                                value,
                                                            }) => (
                                                                <MenuItem
                                                                    value={
                                                                        value
                                                                    }
                                                                    key={value}
                                                                >
                                                                    {label}
                                                                </MenuItem>
                                                            ),
                                                        )}
                                                    </Select>
                                                    {error && (
                                                        <Box
                                                            color="error.main"
                                                            fontSize="0.75rem"
                                                            mt={0.5}
                                                            ml={1.5}
                                                        >
                                                            {error.message}
                                                        </Box>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </FormControl>
                                </Col>
                                <Col xs={6}>
                                    <FormControl
                                        fullWidth
                                        className="mb-3 form-control-bg-white"
                                    >
                                        <TextField
                                            label="Token Key"
                                            {...register(
                                                "expected_response.token_key",
                                                {
                                                    required:
                                                        "Token key is required",
                                                },
                                            )}
                                            error={
                                                !!(
                                                    errors.expected_response as any
                                                )?.token_key
                                            }
                                            helperText={
                                                (
                                                    errors.expected_response as any
                                                )?.token_key?.message as string
                                            }
                                            style={{
                                                width: "100%",
                                            }}
                                            className="text-field-ui-workaround"
                                            required
                                        />
                                    </FormControl>
                                </Col>
                                <Col xs={6}>
                                    <FormControl
                                        fullWidth
                                        className="mb-3 form-control-bg-white"
                                    >
                                        <TextField
                                            label="Token Expiry Key"
                                            {...register(
                                                "expected_response.token_expiry_key",
                                                {
                                                    validate: (value) => {
                                                        if (
                                                            (tokenExpiryRule ===
                                                                "dynamic_expires_in" ||
                                                                tokenExpiryRule ===
                                                                    "dynamic_expires_at") &&
                                                            !value
                                                        ) {
                                                            return "Token expiry key is required for dynamic expiry rules";
                                                        }
                                                        return true;
                                                    },
                                                },
                                            )}
                                            error={
                                                !!(
                                                    errors.expected_response as any
                                                )?.token_expiry_key
                                            }
                                            helperText={
                                                (
                                                    errors.expected_response as any
                                                )?.token_expiry_key
                                                    ?.message as string
                                            }
                                            style={{
                                                width: "100%",
                                            }}
                                            className="text-field-ui-workaround"
                                        />
                                    </FormControl>
                                </Col>
                                <Col xs={6}>
                                    <FormControl
                                        fullWidth
                                        className="mb-3 form-control-bg-white"
                                    >
                                        <TextField
                                            label="Expires in / seconds"
                                            {...register(
                                                "expected_response.token_expiry_time",
                                                {
                                                    validate: (value) => {
                                                        if (
                                                            tokenExpiryRule ===
                                                                "static" &&
                                                            !value
                                                        ) {
                                                            return "Expires in time is required for static expiry rule";
                                                        }
                                                        if (
                                                            value &&
                                                            (isNaN(value) ||
                                                                Number(value) <=
                                                                    0)
                                                        ) {
                                                            return "Please enter a valid positive number";
                                                        }
                                                        return true;
                                                    },
                                                },
                                            )}
                                            error={
                                                !!(
                                                    errors.expected_response as any
                                                )?.token_expiry_time
                                            }
                                            helperText={
                                                (
                                                    errors.expected_response as any
                                                )?.token_expiry_time
                                                    ?.message as string
                                            }
                                            style={{
                                                width: "100%",
                                            }}
                                            className="text-field-ui-workaround"
                                            type="number"
                                        />
                                    </FormControl>
                                </Col>
                                <Col xs={12}>
                                    <Stack direction="row" alignItems="center">
                                        <Box whiteSpace="nowrap">
                                            Test Result
                                        </Box>
                                        <Divider
                                            style={{
                                                width: "85%",
                                                marginLeft: "auto",
                                            }}
                                        />
                                    </Stack>

                                    <pre>
                                        {testResult &&
                                            JSON.stringify(testResult, null, 2)}
                                    </pre>
                                </Col>
                            </Row>
                        </FormAccordion>
                    </Col>
                </Row>
            </Styled.ModalBodyInnerContainer>
        </Modal.Body>
    );
};

export default Content;
